/*
 * Copyright 2026 Hypergiant Galactic Systems Inc. All rights reserved.
 * This file is licensed to you under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License. You may obtain a copy
 * of the License at https://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software distributed under
 * the License is distributed on an "AS IS" BASIS, WITHOUT WARRANTIES OR REPRESENTATIONS
 * OF ANY KIND, either express or implied. See the License for the specific language
 * governing permissions and limitations under the License.
 */

'use client';

import 'client-only';
import { clsx } from '@accelint/design-foundation/lib/utils';
import ChevronRight from '@accelint/icons/chevron-right';
import { useContext } from 'react';
import { composeRenderProps } from 'react-aria-components/composeRenderProps';
import { Button } from '../button';
import { Icon } from '../icon';
import { PaginationContext } from './context';
import styles from './styles.module.css';
import { isNavigationDisabled } from './utils';
import type { PaginationNextProps } from './types';

/**
 * PaginationNext - Next page navigation button
 *
 * @example
 * ```tsx
 * <Pagination page={1} total={10}>
 *   <PaginationNext onPress={(page) => console.log('Next page:', page)} />
 * </Pagination>
 * ```
 *
 * @param props - {@link PaginationNextProps}
 * @param props.className - Optional CSS class name.
 * @param props.onPress - Handler called when the next button is pressed.
 * @returns The rendered PaginationNext component.
 */
export function PaginationNext({ className, onPress }: PaginationNextProps) {
  const { page, total, setPage } = useContext(PaginationContext);

  return (
    <Button
      className={composeRenderProps(className, (className) =>
        clsx(styles.button, className),
      )}
      color='mono-bold'
      variant='icon'
      isDisabled={page >= total || isNavigationDisabled(total, page)}
      onPress={() => {
        setPage(page + 1);
        onPress?.(page + 1);
      }}
      aria-label='Next page'
    >
      <Icon>
        <ChevronRight />
      </Icon>
    </Button>
  );
}
